'use client';

import { c as _c } from "react/compiler-runtime";
import { SendIcon } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import { useCommentsContext } from '@/contexts/comments.context';
import { useCreatePostComment } from '@/hooks/queries/comment.query';
import { useIsHydrated } from '@/hooks/use-is-hydrated';
import { useSession } from '@/lib/auth-client';
import { Spinner } from '../ui/spinner';
import { CommentEditor } from './comment-editor';
import { UnauthenticatedOverlay } from './unauthenticated-overlay';
export function CommentPost() {
  const $ = _c(44);
  const {
    slug
  } = useCommentsContext();
  const [content, setContent] = useState("");
  const [tabsValue, setTabsValue] = useState("write");
  const isHydrated = useIsHydrated();
  const {
    data: session,
    isPending: isSessionLoading
  } = useSession();
  const locale = useLocale();
  const t = useTranslations();
  let t0;
  if ($[0] !== slug) {
    t0 = {
      slug
    };
    $[0] = slug;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t) {
    t1 = () => {
      setContent("");
      toast.add({
        type: "success",
        title: t("success.comment-posted"),
        data: {
          testId: "comment-posted-toast"
        }
      });
      setTabsValue("write");
    };
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    mutate: createComment,
    isPending: isCreating
  } = useCreatePostComment(t0, t1);
  let t2;
  if ($[4] !== content || $[5] !== createComment || $[6] !== isCreating || $[7] !== locale || $[8] !== slug || $[9] !== t) {
    t2 = function submitComment(e) {
      e?.preventDefault();
      if (isCreating) {
        return;
      }
      if (!content.trim()) {
        toast.add({
          type: "error",
          title: t("error.comment-cannot-be-empty")
        });
        return;
      }
      createComment({
        slug,
        content: content.trim(),
        locale
      });
    };
    $[4] = content;
    $[5] = createComment;
    $[6] = isCreating;
    $[7] = locale;
    $[8] = slug;
    $[9] = t;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const submitComment = t2;
  if (!isHydrated) {
    let t3;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="flex h-32.5 items-center justify-center"><Spinner /></div>;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    return t3;
  }
  const isAuthenticated = session !== null && !isSessionLoading;
  const disabled = !isAuthenticated || isCreating;
  let t3;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = e_0 => {
      setContent(e_0.target.value);
    };
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = value => {
      setTabsValue(value as 'write' | 'preview');
    };
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t) {
    t5 = t("blog.comments.placeholder");
    $[14] = t;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== content || $[17] !== disabled || $[18] !== submitComment || $[19] !== t5 || $[20] !== tabsValue) {
    t6 = <CommentEditor value={content} onChange={t3} tabsValue={tabsValue} onTabsValueChange={t4} onModEnter={submitComment} placeholder={t5} disabled={disabled} data-testid="comment-textarea-post" />;
    $[16] = content;
    $[17] = disabled;
    $[18] = submitComment;
    $[19] = t5;
    $[20] = tabsValue;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== content || $[23] !== disabled) {
    t7 = disabled || !content.trim();
    $[22] = content;
    $[23] = disabled;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== t) {
    t8 = t("blog.comments.send-comment");
    $[25] = t;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== content || $[28] !== disabled) {
    t9 = disabled || !content.trim();
    $[27] = content;
    $[28] = disabled;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <SendIcon className="size-4" />;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== t7 || $[32] !== t8 || $[33] !== t9) {
    t11 = <Button variant="ghost" size="icon-xs" className="absolute right-3 bottom-3" type="submit" disabled={t7} aria-label={t8} aria-disabled={t9} data-testid="comment-submit-button">{t10}</Button>;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== isAuthenticated) {
    t12 = isAuthenticated ? null : <UnauthenticatedOverlay />;
    $[35] = isAuthenticated;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== t11 || $[38] !== t12 || $[39] !== t6) {
    t13 = <div className="relative">{t6}{t11}{t12}</div>;
    $[37] = t11;
    $[38] = t12;
    $[39] = t6;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== submitComment || $[42] !== t13) {
    t14 = <form onSubmit={submitComment}>{t13}</form>;
    $[41] = submitComment;
    $[42] = t13;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  return t14;
}